/**
 * Offline Storage - IndexedDB cache for API responses
 * Cache is used when offline; TTL extended when offline
 */
import { initDB } from './offlineDb';

const STORE_NAME = 'api-cache';
const DEFAULT_MAX_AGE = 24 * 60 * 60 * 1000; // 24 hours
const OFFLINE_MAX_AGE = 7 * 24 * 60 * 60 * 1000; // 7 days when offline

/**
 * Build cache key from URL (handles query params)
 */
const buildCacheKey = (url) => url;

/**
 * Store API response in cache
 */
export const storeApiResponse = async (url, data, method = 'GET', entity = null) => {
  try {
    if (method !== 'GET') return;
    const database = await initDB();
    const transaction = database.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const key = buildCacheKey(url);
    await store.put({
      key,
      url,
      data,
      entity: entity || null,
      timestamp: Date.now(),
      method,
    });
  } catch (error) {
    console.error('[Offline Storage] Error storing:', error);
  }
};

/**
 * Get cached response - returns null if expired
 * When offline, use longer TTL (7 days)
 */
export const getApiResponse = async (url) => {
  try {
    const database = await initDB();
    const transaction = database.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const key = buildCacheKey(url);
    const result = await new Promise((resolve, reject) => {
      const req = store.get(key);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
    if (!result) return null;

    const isOffline = !navigator.onLine;
    const maxAge = isOffline ? OFFLINE_MAX_AGE : DEFAULT_MAX_AGE;
    const cacheAge = Date.now() - result.timestamp;
    if (cacheAge >= maxAge) return null;

    return result.data;
  } catch (error) {
    console.error('[Offline Storage] Error retrieving:', error);
    return null;
  }
};

/**
 * Invalidate cache entries by URL prefix (e.g. 'accounting/partners')
 */
export const invalidateCacheByPrefix = async (urlPrefix) => {
  try {
    const database = await initDB();
    const transaction = database.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const index = store.index('url');
    const range = IDBKeyRange.bound(urlPrefix, urlPrefix + '\uffff');
    return new Promise((resolve, reject) => {
      const req = index.openCursor(range);
      req.onsuccess = (e) => {
        const cursor = e.target.result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        } else resolve();
      };
      req.onerror = () => reject(req.error);
    });
  } catch (error) {
    console.error('[Offline Storage] Error invalidating:', error);
  }
};

/**
 * Clear entries older than maxAge (default 7 days)
 */
export const clearOldCache = async (maxAgeDays = 7) => {
  try {
    const database = await initDB();
    const transaction = database.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const cutoff = Date.now() - maxAgeDays * 24 * 60 * 60 * 1000;
    return new Promise((resolve, reject) => {
      const req = store.index('timestamp').openCursor();
      req.onsuccess = (e) => {
        const cursor = e.target.result;
        if (cursor) {
          if (cursor.value.timestamp < cutoff) cursor.delete();
          cursor.continue();
        } else resolve();
      };
      req.onerror = () => reject(req.error);
    });
  } catch (error) {
    console.error('[Offline Storage] Error clearing old cache:', error);
  }
};

/**
 * Clear all cache
 */
export const clearAllCache = async () => {
  try {
    const database = await initDB();
    const transaction = database.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    await store.clear();
  } catch (error) {
    console.error('[Offline Storage] Error clearing cache:', error);
  }
};
